// Part number / name suggestions under a text box. The list floats above the page (it is never cut off by the card it's in),
// shows up to 50 inventory parts with a scrollbar, then part numbers used on past invoices, and — when nothing matches —
// offers to add the part or link the typed number to a part you already have. ↑ ↓ to move, Enter to pick, Esc to close.
import { useEffect, useLayoutEffect, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useApi, dollars, partMatches, normKey, type Row } from './lib';

/** Best first: the exact part #, then part #s that start with it, then everything else that matches (A–Z). */
export function rankParts(catalog: Row[], q: string, limit = 50) {
  const k = normKey(q);
  if (k.length < 2) return { items: [] as Row[], total: 0 };
  const all = catalog.filter(p => partMatches(p, q));
  const score = (p: Row) => {
    const s = normKey(p.sku), alts = String(p.alt_numbers || '').split(',').map(normKey);
    if (s === k || alts.includes(k) || normKey(p.upc) === k) return 0;
    if (s.startsWith(k)) return 1;
    if (s.includes(k)) return 2;
    return 3;
  };
  all.sort((a, b) => score(a) - score(b) || String(a.name).localeCompare(String(b.name)));
  return { items: all.slice(0, limit), total: all.length };
}

/** Part numbers on past invoices that aren't in inventory (office only — it carries prices). */
export function usePartHistory(q: string, on = true) {
  const [dq, setDq] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const h = useApi<Row[]>(on && normKey(dq).length >= 3 ? `/api/parts/history?q=${encodeURIComponent(dq)}` : null, [dq, on]);
  return h.data || [];
}

/** Keyboard for the list: returns the highlighted row and a key handler for the input. Enter returns the index to pick (or -1). */
export function useSuggestKeys(count: number, open: boolean) {
  const [hi, setHi] = useState(0);
  useEffect(() => setHi(0), [count]);
  const onKey = (e: { key: string; preventDefault: () => void }): number | null => {
    if (!open || !count) return null;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(count - 1, h + 1)); return null; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); return null; }
    if (e.key === 'Enter') { e.preventDefault(); return hi; }
    return null;
  };
  return { hi, setHi, onKey };
}

type Pos = { left: number; width: number; top?: number; bottom?: number; maxHeight: number };
function usePos(anchor: RefObject<HTMLElement | null>, open: boolean): Pos | null {
  const [pos, setPos] = useState<Pos | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth, vh = window.innerHeight;
      const width = Math.min(Math.max(r.width, 560), vw - 16);
      const left = Math.max(8, Math.min(r.left, vw - width - 8));
      const below = vh - r.bottom - 12, above = r.top - 12;
      // Open downward when there's room for a few rows, else upward.
      if (below >= 240 || below >= above) setPos({ left, width, top: r.bottom + 4, maxHeight: Math.max(160, Math.min(440, below)) });
      else setPos({ left, width, bottom: vh - r.top + 4, maxHeight: Math.max(160, Math.min(440, above)) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [anchor, open]);
  return open ? pos : null;
}

export function PartSuggest({ anchor, open, q, items, total, hist = [], hi, onHover, onPick, onPickHist, onAddNew, onLink, showPrice = true }: {
  anchor: RefObject<HTMLElement | null>; open: boolean; q: string; items: Row[]; total: number; hist?: Row[]; hi: number; onHover: (i: number) => void;
  onPick: (p: Row) => void; onPickHist?: (h: Row) => void; onAddNew?: () => void; onLink?: () => void; showPrice?: boolean;
}) {
  const pos = usePos(anchor, open);
  useEffect(() => { document.querySelector('.ps-pop .on')?.scrollIntoView({ block: 'nearest' }); }, [hi]);
  if (!open || !pos || normKey(q).length < 2) return null;
  const none = !items.length && !hist.length;
  const keep = (e: { preventDefault: () => void }) => e.preventDefault(); // keep the focus in the input
  const row = (i: number, body: ReactNode, pick: () => void) => (
    <li key={i}><button type="button" className={i === hi ? 'on' : ''} onMouseDown={e => { keep(e); pick(); }} onMouseEnter={() => onHover(i)}>{body}</button></li>
  );
  return createPortal(
    <div className="ps-pop" style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }} onMouseDown={keep} role="listbox">
      {items.length > 0 && <div className="ps-head">{total > items.length ? `${items.length} of ${total} found — type more to narrow` : `${total} found in inventory`}</div>}
      <ul>
        {items.map((p, i) => row(i, <>
          <b className="ps-sku">{p.sku}</b><span className="ps-name">{p.name}</span>
          <span className="ps-stock">{p.track_qty === 0 ? 'not counted' : `${p.stock} on hand`}</span>
          {showPrice && <span className="ps-price">{p.price ? dollars(p.price) : <em>no price</em>}</span>}
        </>, () => onPick(p)))}
      </ul>
      {hist.length > 0 && <>
        <div className="ps-head">Used before, not in inventory</div>
        <ul>{hist.map((h, j) => row(items.length + j, <>
          <b className="ps-sku">{h.part_no}</b><span className="ps-name">{h.description}<small>{h.invoice_number || h.wo_number}{h.vendor ? ` · ${h.vendor}` : ''}</small></span>
          <span className="ps-stock" />{showPrice && <span className="ps-price">{dollars(h.unit_price)}</span>}
        </>, () => onPickHist?.(h)))}</ul>
      </>}
      {none && <div className="ps-none"><b>Not in inventory — "{q}"</b>
        <div className="ps-actions">
          {onAddNew && <button type="button" className="btn secondary small" onMouseDown={e => { keep(e); onAddNew(); }}>+ Add as new part</button>}
          {onLink && <button type="button" className="btn secondary small" onMouseDown={e => { keep(e); onLink(); }}>Link to an existing part…</button>}
        </div>
        <small className="muted">Or just fill in the line and add it — it's used on this job without going into inventory.</small>
      </div>}
    </div>,
    document.body,
  );
}
